import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';

import { useStore } from '@/shared/store/store-context';

import { emptyBannerForm, emptyRailForm, toBlockCreate } from '../api/mappers';
import { BlockEditor } from '../components/editor/block-editor';
import { BlockKindChooser } from '../components/editor/block-kind-chooser';
import { useCreateBlock } from '../hooks/use-block-mutations';
import type { BlockFormInput, BlockKind } from '../schemas/block-form-schema';

const STORE_KIND_ROUTE = '/_authed/_active/s/$storeId/content/blocks/new/$kind' as const;
const ORG_KIND_ROUTE = '/_authed/_active/org/shared-blocks/new/$kind' as const;

function backTo(storeId: string | null) {
  return storeId === null
    ? ({ to: '/org/shared-blocks' } as const)
    : ({ to: '/s/$storeId/content/blocks', params: { storeId } } as const);
}

/** Step one: pick the kind. Each card opens that kind's editor. */
function KindChooser({ storeId }: { storeId: string | null }) {
  const { t } = useTranslation('content');
  return (
    <PageContent>
      <PageHeader
        title={storeId === null ? t('block.createSharedTitle') : t('block.createTitle')}
        description={t('block.kindDescription')}
        backTo={backTo(storeId)}
        backLabel={storeId === null ? t('blocks.sharedTitle') : t('blocks.title')}
      />
      <BlockKindChooser storeId={storeId} />
    </PageContent>
  );
}

function emptyForm(kind: BlockKind, storeId: string | null): BlockFormInput {
  return kind === 'banner'
    ? emptyBannerForm()
    : emptyRailForm(storeId === null ? 'shared' : 'store');
}

/** Step two: the editor for the chosen kind. Saving lands on the new block's edit page. */
function KindEditor({ storeId, kind }: { storeId: string | null; kind: BlockKind }) {
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const create = useCreateBlock(storeId);

  return (
    <PageContent>
      <PageHeader
        title={
          storeId === null
            ? t('block.createSharedKindTitle', { kind: t(`kind.${kind}`) })
            : t('block.createKindTitle', { kind: t(`kind.${kind}`) })
        }
        backTo={
          storeId === null
            ? { to: '/org/shared-blocks/new' }
            : { to: '/s/$storeId/content/blocks/new', params: { storeId } }
        }
        backLabel={t('block.changeKind')}
      />
      <BlockEditor
        key={kind}
        storeId={storeId}
        mode="create"
        defaultValues={emptyForm(kind, storeId)}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const created = await create.mutateAsync(toBlockCreate(values));
          await (storeId === null
            ? navigate({
                to: '/org/shared-blocks/$blockId',
                params: { blockId: created.id },
                replace: true,
              })
            : navigate({
                to: '/s/$storeId/content/blocks/$blockId',
                params: { storeId, blockId: created.id },
                replace: true,
              }));
        }}
      />
    </PageContent>
  );
}

function StoreKindEditor() {
  const store = useStore();
  const { kind } = useParams({ from: STORE_KIND_ROUTE });
  return <KindEditor storeId={store.id} kind={kind} />;
}

function SharedKindEditor() {
  const { kind } = useParams({ from: ORG_KIND_ROUTE });
  return <KindEditor storeId={null} kind={kind} />;
}

function StoreKindChooser() {
  const store = useStore();
  return <KindChooser storeId={store.id} />;
}

export function BlockCreatePage() {
  return (
    <FeatureErrorBoundary name="content.blocks.create">
      <StoreKindChooser />
    </FeatureErrorBoundary>
  );
}

export function BlockCreateKindPage() {
  return (
    <FeatureErrorBoundary name="content.blocks.createKind">
      <StoreKindEditor />
    </FeatureErrorBoundary>
  );
}

export function SharedBlockCreatePage() {
  return (
    <FeatureErrorBoundary name="content.sharedBlocks.create">
      <KindChooser storeId={null} />
    </FeatureErrorBoundary>
  );
}

export function SharedBlockCreateKindPage() {
  return (
    <FeatureErrorBoundary name="content.sharedBlocks.createKind">
      <SharedKindEditor />
    </FeatureErrorBoundary>
  );
}
